"use client"

import { useEffect, useState } from "react"
import { CheckCircle2, Loader2, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TurnstileWidget } from "@/components/security/turnstile-widget"

const TOPICS = [
  "Gameplay problem",
  "Account help",
  "Report content",
  "Feedback",
  "Other",
] as const

export function SupportForm() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [topic, setTopic] = useState<(typeof TOPICS)[number]>("Gameplay problem")
  const [message, setMessage] = useState("")
  const [pageUrl, setPageUrl] = useState("")
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    const referencedPage = query.get("page")
    if (referencedPage) setPageUrl(referencedPage.slice(0, 500))
  }, [])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    setSuccess("")
    setLoading(true)

    try {
      const response = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          topic,
          message,
          pageUrl: pageUrl || undefined,
          website: "",
          turnstileToken,
        }),
      })
      const result = (await response.json()) as {
        error?: string
        message?: string
      }

      if (!response.ok) {
        setError(result.error || "Your message could not be sent.")
        setTurnstileToken("")
        setTurnstileResetKey((key) => key + 1)
        return
      }

      setSuccess(result.message || "Your message was sent.")
      setMessage("")
      setPageUrl("")
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    } catch {
      setError("Your message could not be sent. Please try again.")
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-[32px] border border-[#d8d0c4] bg-[#fffaf3] p-6 shadow-[0_24px_70px_rgba(77,62,48,0.09)] sm:p-8 lg:p-10"
    >
      <div className="flex items-start justify-between gap-5">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.12em] text-[#b83f34]">
            Direct support
          </p>
          <h2 className="mt-2 text-3xl font-black tracking-[-0.05em] text-[#171717]">
            Tell us what happened
          </h2>
        </div>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#171717] bg-[#ff6f61] text-[#171717]">
          <Send className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>

      {error ? (
        <div
          role="alert"
          className="mt-6 rounded-2xl border border-rose-700/25 bg-rose-50 p-4 text-sm font-medium text-rose-800"
        >
          {error}
        </div>
      ) : null}
      {success ? (
        <div
          role="status"
          className="mt-6 flex items-start gap-3 rounded-2xl border border-emerald-700/20 bg-emerald-50 p-4 text-sm font-medium text-emerald-900"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{success}</span>
        </div>
      ) : null}

      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="support-name" className="text-sm font-bold text-[#171717]">
            Name
          </label>
          <Input
            id="support-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            className="h-12 rounded-2xl"
            maxLength={80}
            required
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="support-email" className="text-sm font-bold text-[#171717]">
            Email
          </label>
          <Input
            id="support-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            className="h-12 rounded-2xl"
            maxLength={254}
            required
          />
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor="support-topic" className="text-sm font-bold text-[#171717]">
          Topic
        </label>
        <select
          id="support-topic"
          value={topic}
          onChange={(event) =>
            setTopic(event.target.value as (typeof TOPICS)[number])
          }
          className="h-12 w-full rounded-2xl border border-[#d8d0c4] bg-white px-4 text-sm font-semibold text-[#171717] outline-none transition-colors focus:border-[#171717]"
        >
          {TOPICS.map((value) => (
            <option value={value} key={value}>
              {value}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor="support-page" className="text-sm font-bold text-[#171717]">
          Page or room code
          <span className="ml-2 font-normal text-[#7b7268]">Optional</span>
        </label>
        <Input
          id="support-page"
          value={pageUrl}
          onChange={(event) => setPageUrl(event.target.value)}
          placeholder="Paste the page URL or room code"
          className="h-12 rounded-2xl"
          maxLength={500}
        />
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor="support-message" className="text-sm font-bold text-[#171717]">
          What can we help with?
        </label>
        <textarea
          id="support-message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Include what you expected, what happened, and any steps that reproduce it."
          className="min-h-40 w-full resize-y rounded-2xl border border-[#d8d0c4] bg-white px-4 py-3 text-sm leading-6 text-[#171717] outline-none transition-colors placeholder:text-[#9b9186] focus:border-[#171717]"
          minLength={20}
          maxLength={5_000}
          required
        />
        <p className="text-right text-xs text-[#7b7268]">
          {message.length.toLocaleString("en-US")} / 5,000
        </p>
      </div>

      <TurnstileWidget
        onTokenChange={setTurnstileToken}
        resetKey={turnstileResetKey}
        theme="light"
      />

      <Button
        type="submit"
        className="mt-5 h-12 w-full rounded-full border border-[#171717] text-base font-black shadow-[inset_0_-3px_0_rgba(23,23,23,0.14)]"
        disabled={loading || !turnstileToken}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            Sending message...
          </>
        ) : (
          "Send to support"
        )}
      </Button>
      <p className="mt-4 text-center text-xs leading-5 text-[#7b7268]">
        Replies come from higherlower@adeptaoperations.com. Do not include
        passwords or payment details.
      </p>
    </form>
  )
}
